import React, { useRef } from 'react';
import {
  Box,
  Button,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Flex,
  IconButton,
  Checkbox,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FaChevronDown, FaSearch, FaEdit, FaPlus } from 'react-icons/fa';
import PropTypes from 'prop-types';

const ITEM_HEIGHT = 44;
const LIST_HEIGHT = 200;

const MerchantFilterDropdown = ({
  // Filter state
  searchTerm,
  setSearchTerm,
  selectedMerchantText,
  isPopoverOpen,
  openPopover,
  closePopover,
  isDisabled,
  isLoadingMerchants,

  // Merchant data
  filteredMerchants,

  // Merchant actions
  onSelectAll,
  onMerchantToggle,
  isSelectAllChecked,
  isMerchantChecked,
  onApply,
  onCancel,
  onClearSelection,

  // Unassigned state
  excludeUnassigned,
  onUnassignedToggle,
  pendingExcludeUnassigned,

  // Group data
  merchantGroups,
  selectedGroup,

  // Group actions
  onGroupSelection,
  onCreateGroup,
  onEditGroup,

  // Customization props
  showGroups = true,
  showSearch = true,
  showSelectAll = true,
  showUnassigned = false,
  maxHeight = '500px',
  buttonWidth = { base: '100%', md: '300px' },
}) => {
  const isAnyMerchantSelected =
    filteredMerchants.some((m) => isMerchantChecked(m)) || false;

  const listRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: filteredMerchants.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  });

  return (
    <Box w={buttonWidth}>
      <Popover
        placement="bottom-start"
        isOpen={isPopoverOpen}
        onClose={closePopover}
        matchWidth={true}
        strategy="absolute"
        isLazy
        lazyBehavior="unmount"
      >
        <PopoverTrigger>
          <Button
            w="100%"
            justifyContent="space-between"
            rightIcon={<FaChevronDown />}
            variant="outline"
            borderColor="#D9D9D9"
            borderRadius="12px"
            color="#292D32"
            bg="white"
            fontWeight="500"
            h="40px"
            isDisabled={isDisabled}
            onClick={() => (isPopoverOpen ? closePopover() : openPopover())}
          >
            <Text textAlign="left" flex="1" noOfLines={1} fontSize="14px">
              {selectedMerchantText}
            </Text>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          maxH={maxHeight}
          minW="350px"
          w="350px"
          borderRadius="12px"
          border="1px solid #D9D9D9"
          bg="white"
          shadow="md"
          _focus={{ outline: 'none' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <PopoverBody p="0" w="100%">
            <VStack align="stretch" spacing="0" w="100%">
              {/* Search Bar - Conditional */}
              {showSearch && (
                <Box p="3" borderBottom="1px solid #f0f0f0">
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <FaSearch color="#9CA3AF" size="12px" />
                    </InputLeftElement>
                    <Input
                      id="dropdown-merchant-search"
                      placeholder="Search merchants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      borderRadius="6px"
                      fontSize="0.875rem"
                      _focus={{
                        borderColor: '#3182ce',
                        boxShadow: 'none',
                      }}
                      isDisabled={isDisabled}
                    />
                  </InputGroup>
                </Box>
              )}

              <Box flex="1" overflowY="auto" minH="0">
                {/* Groups Section - Conditional */}
                {showGroups && (
                  <Box borderBottom="1px solid #f0f0f0" p="3">
                    <Text
                      fontSize="0.75rem"
                      fontWeight="600"
                      color="gray.600"
                      mb="2"
                    >
                      GROUPS
                    </Text>
                    <Flex
                      wrap="wrap"
                      gap="2"
                      maxH="130px"
                      overflowY="auto"
                      overflowX="hidden"
                    >
                      {merchantGroups.map((group) => {
                        const isSelected = selectedGroup?.id === group.id;
                        return (
                          <Flex
                            key={group.id}
                            align="center"
                            px="3"
                            py="1"
                            borderRadius="6px"
                            bg={isSelected ? 'blue.500' : 'gray.100'}
                            color={isSelected ? 'white' : 'gray.700'}
                            fontSize="0.85rem"
                            fontWeight="500"
                            cursor="pointer"
                            _hover={{
                              bg: isSelected ? 'blue.600' : 'gray.200',
                            }}
                            onClick={() => onGroupSelection(group)}
                          >
                            <Text noOfLines={1} maxW="150px">
                              {group.groupName} ({group.merchants?.length || 0})
                            </Text>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              icon={<FaEdit />}
                              ml="1"
                              color={isSelected ? 'white' : 'gray.500'}
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditGroup(group);
                              }}
                              _hover={{ bg: 'transparent' }}
                              isDisabled={isDisabled}
                            />
                          </Flex>
                        );
                      })}

                      {/* Add group button at end */}
                      <Tooltip label="Create Group" placement="top">
                        <IconButton
                          icon={<FaPlus />}
                          size="sm"
                          borderRadius="full"
                          bg="gray.100"
                          onClick={onCreateGroup}
                          isDisabled={isDisabled}
                        />
                      </Tooltip>
                    </Flex>
                  </Box>
                )}

                {/* Select All / Clear Selection - Conditional */}
                {showSelectAll && (
                  <Flex
                    p="2"
                    borderBottom="1px solid #f0f0f0"
                    justify="space-between"
                    align="center"
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      isDisabled={isDisabled || isLoadingMerchants}
                      onClick={() => onSelectAll(filteredMerchants)}
                    >
                      {isLoadingMerchants
                        ? 'Loading...'
                        : isSelectAllChecked(filteredMerchants)
                          ? 'Clear All'
                          : 'Select All'}
                    </Button>

                    {(isAnyMerchantSelected ||
                      selectedGroup ||
                      excludeUnassigned) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        color="red.400"
                        onClick={onClearSelection}
                        isDisabled={isDisabled || isLoadingMerchants}
                      >
                        Clear
                      </Button>
                    )}
                  </Flex>
                )}

                {/* Unassigned Option - Only shown when showUnassigned is true */}
                {showUnassigned && (
                  <Box p="3" borderBottom="1px solid #f0f0f0">
                    <Checkbox
                      id="dropdown-unassigned"
                      isChecked={pendingExcludeUnassigned}
                      onChange={(e) => onUnassignedToggle(e.target.checked)}
                      w="100%"
                      colorScheme="blue"
                      isDisabled={isDisabled}
                    >
                      <Text fontSize="0.875rem" fontWeight="500">
                        Unassigned (stations without merchant)
                      </Text>
                    </Checkbox>
                  </Box>
                )}

                {/* Merchant List — Virtual Scrolling */}
                <Box
                  ref={listRef}
                  h={`${LIST_HEIGHT}px`}
                  overflowY="auto"
                  overflowX="hidden"
                  w="100%"
                >
                  {filteredMerchants.length > 0 ? (
                    <Box
                      position="relative"
                      h={`${virtualizer.getTotalSize()}px`}
                      w="100%"
                    >
                      {virtualizer.getVirtualItems().map((virtualRow) => {
                        const merchant = filteredMerchants[virtualRow.index];
                        return (
                          <Box
                            key={merchant.merchantId}
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h={`${virtualRow.size}px`}
                            transform={`translateY(${virtualRow.start}px)`}
                          >
                            <Checkbox
                              id={`dropdown-merchant-${merchant.merchantId}`}
                              isChecked={isMerchantChecked(merchant)}
                              onChange={(e) =>
                                onMerchantToggle(merchant, e.target.checked)
                              }
                              p="3"
                              w="100%"
                              h="100%"
                              borderRadius="0"
                              _hover={{ bg: 'gray.50' }}
                              colorScheme="blue"
                              isDisabled={isDisabled}
                            >
                              <Text
                                fontSize="0.875rem"
                                fontWeight="500"
                                isTruncated
                                maxW="290px"
                                pr="15px"
                                title={merchant.displayName || merchant.name}
                              >
                                {merchant.displayName || merchant.name}
                              </Text>
                            </Checkbox>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box p="3" textAlign="center">
                      <Text fontSize="0.875rem" color="gray.500">
                        No merchants found
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Sticky Apply/Cancel - Always Visible */}
              <Box
                borderTop="1px solid #f0f0f0"
                p="3"
                bg="white"
                flexShrink={0}
                position="sticky"
                bottom="0"
              >
                <HStack spacing="2" justify="flex-end" w="100%">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onCancel}
                    isDisabled={isDisabled}
                    minW="60px"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={onApply}
                    isDisabled={isDisabled}
                    minW="60px"
                  >
                    Apply
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

MerchantFilterDropdown.propTypes = {
  // Filter state
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  selectedMerchantText: PropTypes.string.isRequired,
  isPopoverOpen: PropTypes.bool.isRequired,
  openPopover: PropTypes.func.isRequired,
  closePopover: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isLoadingMerchants: PropTypes.bool,

  // Merchant data
  filteredMerchants: PropTypes.arrayOf(PropTypes.object).isRequired,

  // Merchant actions
  onSelectAll: PropTypes.func.isRequired,
  onMerchantToggle: PropTypes.func.isRequired,
  isSelectAllChecked: PropTypes.func.isRequired,
  isMerchantChecked: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,

  // Unassigned state
  excludeUnassigned: PropTypes.bool,
  onUnassignedToggle: PropTypes.func,
  pendingExcludeUnassigned: PropTypes.bool,

  // Group data
  merchantGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedGroup: PropTypes.object,
  isGroupsLoading: PropTypes.bool,

  // Group actions
  onGroupSelection: PropTypes.func.isRequired,
  onCreateGroup: PropTypes.func.isRequired,
  onEditGroup: PropTypes.func.isRequired,

  // Customization props
  showGroups: PropTypes.bool,
  showSearch: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  showUnassigned: PropTypes.bool,
  maxHeight: PropTypes.string,
  buttonWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default MerchantFilterDropdown;
